/**
 * Hjelpemetoder for Perioder basert på PlainDate
 */

import {Temporal} from "temporal-polyfill";
import {lesPlainDateSerialisert, lesPlainDateSerialisertOpt, skrivPlainDateSerialisert, skrivPlainDateSerialisertOpt, zonedDateTimeSluttPåDagenFraPlainDate, zonedDateTimeStartPåDagenFraPlainDate} from "./temporal";
import {erNil} from "./nilsjekk";
import {DatoPeriodeUtenTid} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {nil} from "felles-ng2/typer/nullish";
import {ZonedDateTimePeriodeLukket} from "felles-ng2/typer/ZonedDateTimePeriode";
import {Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import PlainDate = Temporal.PlainDate;
import PlainDateLike = Temporal.PlainDateLike;

export type PlainDatePeriode = { start: PlainDate, slutt: PlainDate };
export type PlainDatePeriodeMuligÅpenSlutt = { start: PlainDate, slutt: PlainDate | null };

export function lesDatoPeriodeUtenTid(periode: DatoPeriodeUtenTid): PlainDatePeriodeMuligÅpenSlutt {
    return {start: lesPlainDateSerialisert(periode.start), slutt: lesPlainDateSerialisertOpt(periode.slutt)};
}

export function lesDatoPeriodeUtenTidOpt(periode: DatoPeriodeUtenTid | nil): PlainDatePeriodeMuligÅpenSlutt | null {
    if (erNil(periode)) return null;
    return lesDatoPeriodeUtenTid(periode);
}

export function skrivDatoPeriodeUtenTid(periode: PlainDatePeriodeMuligÅpenSlutt): DatoPeriodeUtenTid {
    return {start: skrivPlainDateSerialisert(periode.start), slutt: skrivPlainDateSerialisertOpt(periode.slutt)};
}


/** Returnerer antall kalenderdager i angitt periode.
 * Dvs (antall 'midnatt' man passerer mellom start og slutt) + 1.
 * Merk at beregningen gjøres ihht den tidssone som er angitt i start. */
export function antallDagerIPeriode(periode: PlainDatePeriode): number {
    return periode.start
      .until(periode.slutt)
      .total({unit: 'days'}) + 1;
}

export function periodeInneholder(periode: PlainDatePeriodeMuligÅpenSlutt, dato: PlainDateLike): boolean {
    if (PlainDate.compare(periode.start, dato) > 0) return false;
    if (periodeHarIkkeÅpenSlutt(periode) && PlainDate.compare(periode.slutt!, dato) < 0) return false;
    return true;
}

export function periodeInneholderNå(periode: PlainDatePeriodeMuligÅpenSlutt, klokke: Klokke): boolean {
    return periodeInneholder(periode, klokke.nåSomPlainDate());
}

export function periodeHarÅpenSlutt(periode: PlainDatePeriodeMuligÅpenSlutt): boolean {
    return erNil(periode.slutt);
}

export function periodeHarIkkeÅpenSlutt(periode: PlainDatePeriodeMuligÅpenSlutt): boolean {
    return !periodeHarÅpenSlutt(periode);
}

export function periodeSlutterFør(periode: PlainDatePeriodeMuligÅpenSlutt | null, dato: PlainDateLike): boolean {
    if (erNil(periode)) return false;
    if (periodeHarÅpenSlutt(periode)) return false;
    return PlainDate.compare(periode.slutt!, dato) < 0;
}

export function periodeErPassert(periode: PlainDatePeriodeMuligÅpenSlutt | null, klokke: Klokke): boolean {
    return periodeSlutterFør(periode, klokke.nåSomPlainDate());
}

export function periodeOverlapper(a: PlainDatePeriodeMuligÅpenSlutt, b: PlainDatePeriodeMuligÅpenSlutt): boolean {
    return periodeInneholder(a, b.start) || periodeInneholder(b, a.start);
}

export function plainDatePeriodeSomZonedDateTimePeriode(periode: PlainDatePeriode, tidssone:Tidssoneinformasjon): ZonedDateTimePeriodeLukket {
    return new ZonedDateTimePeriodeLukket(
      zonedDateTimeStartPåDagenFraPlainDate(periode.start,tidssone),
      zonedDateTimeSluttPåDagenFraPlainDate(periode.slutt,tidssone));
}
